import { Component, Inject, Input } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { Router } from '@angular/router';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import moment from 'moment';
import { IBranchModel } from 'src/app/logic/branch/models/branch.model';
import { IExchangeModel } from 'src/app/logic/exchanges/model/exchange.model';
import { AlertsService } from 'src/app/services/alerts.service';
import { BranchesService } from 'src/app/services/branches.service';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { HttpService } from 'src/app/services/http-service.service';
import { OperatingHoursService } from 'src/app/services/operating-hours.service';
import { ExchangeService } from 'src/app/services/exchange.service';
import {
  CreateQuotationDto,
  QuotationsService,
} from 'src/app/services/quotations.service';
import {
  CreateReservationDto,
  ReservationsService,
} from 'src/app/services/reservations.service';
import { ExchangeRateCalculatorService } from 'src/app/services/exchange-rate-calculator.service';
import { LoginPromptComponent } from 'src/app/components/login-prompt/login-prompt.component';
import { LoginPromptDialogData } from 'src/app/components/login-prompt/login-prompt-dialog.data';
import { formatOvernightConfirmationDeadline } from '../utils/overnight-confirmation-deadline.util';
import { savePendingQuotationIds, clearPendingQuotationIds } from 'src/app/utils/pending-quotation-confirm.storage';
import { createReservationIdempotencyKey } from 'src/app/utils/reservation-idempotency-key.util';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import {
  applyEffectivePromotionRate,
  mapPromotionExchangeToModel,
} from '../utils/promotion-rate.util';
import {
  MEXICO_TIMEZONE,
  mexicoWallClockToIso,
} from '../utils/mexico-datetime.util';
import { getApiErrorMessage } from '../utils/api-error.util';
import { getForeignAmountFromCurrencyPair } from '../utils/reservation-foreign-amount.util';
import { PromotionDetailPromotion } from '../promotion-detail-modal/promotion-display.util';

export interface PromotionAvailabilityView {
  operationLimit: number;
  usedAmount: number;
  availableAmount: number;
  currencyCode: string;
  currencyName: string;
  regularRate: number | null;
  effectivePromotionRate: number | null;
}

export interface PromoSplitPreview {
  promoAmount: number;
  normalAmount: number;
  totalAmount: number;
  currencyCode: string;
  promoRate: number | null;
  regularRate: number | null;
  averageRate: number | null;
}

@Component({
  selector: 'app-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
})
export class ModalComponent {
  private quotationId?: number;
  private normalQuotationId?: number;
  private confirmedFromAmount?: number;
  private confirmedToAmount?: number;
  /** Reused across retries of the same reservation submit attempt. */
  private reservationRequestId?: string;
  reservationStep: number = 1;
  isPromoLocked = false;
  promoAvailability?: PromotionAvailabilityView;
  promoSplitPreview?: PromoSplitPreview;
  showPromoSplitWarning = false;

  @Input() isCotizacion: boolean = true;
  selectedSlotId: string = '';
  availableTimeSlots: any[] = [];
  branchTimezone = MEXICO_TIMEZONE;
  isOutOfHours: boolean = false;
  marketInactive = false;
  stockApprovalRequired = false;
  collectionDateLabel: string = '';
  private collectionDateRaw = '';
  private overnightServerDate = '';
  private overnightServerTime = '';
  isProcessingSiguiente: boolean = false;
  isProcessingCompletar: boolean = false;

  public divisas: any;
  public sucursal: IBranchModel;
  public branches: IBranchModel[] = [];
  public queDivisaTienes!: IExchangeModel;
  public queDivisaQuieres!: IExchangeModel;
  public cotizacion: any;
  public estimatedPoints: number = 0;
  public quotationDate: string = this.formatQuotationDate();

  constructor(
    private readonly _dialogRef: MatDialogRef<ModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly httpService: HttpService,
    private readonly router: Router,
    private readonly alert: AlertsService,
    private readonly _analyticsService: AnalyticsService,
    private readonly dialog: MatDialog,
    private readonly operatingHoursService: OperatingHoursService,
    private readonly exchangeService: ExchangeService,
    private readonly quotationsService: QuotationsService,
    private readonly reservationsService: ReservationsService,
    private readonly exchangeRateCalculator: ExchangeRateCalculatorService,
    private readonly translate: TranslateService,
    private readonly branchesService: BranchesService,
  ) {
    if (data.promotion) {
      this.sucursal = data.sucursal ?? data.promotion.branch;
      this.initPromotionFlow(data.promotion as PromotionDetailPromotion);
    } else {
      this.divisas = data.precios;
      this.sucursal = data.sucursal;
      this.queDivisaTienes = data.tienes;

      if (data.quieres) {
        this.queDivisaQuieres = data.quieres;
      }
    }

    if (this.sucursal?.id) {
      this.generateAvailableTimeSlots();
    }
  }

  onNoClick(): void {
    this.clearReservationCache();
    this.data = 'Test';
    this._dialogRef.close();
  }

  actionTest() {
    this.clearReservationCache();
    this._dialogRef.close();
  }

  anterior() {
    this.reservationStep--;
    if (this.reservationStep === 1) {
      this.showPromoSplitWarning = false;
    }
  }

  modifyPromoAmount(): void {
    this.showPromoSplitWarning = false;
  }

  get hasPromoSplit(): boolean {
    return (
      !!this.promoSplitPreview &&
      this.promoSplitPreview.promoAmount > 0 &&
      this.promoSplitPreview.normalAmount > 0
    );
  }

  siguiente() {
    if (this.isProcessingSiguiente) {
      return;
    }

    if (this.reservationStep === 1) {
      if (this.showPromoSplitWarning) {
        this.proceedFromQuoteStep();
        return;
      }

      const quoteErrorKey = this.getQuoteValidationErrorKey();
      if (quoteErrorKey) {
        this.showQuoteValidationError(quoteErrorKey);
        return;
      }

      if (this.hasPromoSplit) {
        this.showPromoSplitWarning = true;
        return;
      }

      this.proceedFromQuoteStep();
      return;
    }

    if (this.reservationStep === 2) {
      const quoteErrorKey = this.getQuoteValidationErrorKey();
      if (quoteErrorKey) {
        this.showQuoteValidationError(quoteErrorKey);
        return;
      }

      this.isProcessingSiguiente = true;
      this.createQuotationIfNeeded().subscribe({
        next: (response) => {
          this.isProcessingSiguiente = false;
          if (response.success) {
            this.reservationStep++;
            this.loadEstimatedPoints();
          } else {
            this.alert.show({
              title: this.translate.instant('PROFILE_PAGE.GENERIC_ERROR'),
              text:
                response.message ||
                this.translate.instant('MODAL_RESERVATION.QUOTE_CREATE_FAILED'),
              confirmButtonText: this.translate.instant(
                'COMMON_ALERTS.BUTTON_OK',
              ),
            });
          }
        },
        error: (error) => {
          this.isProcessingSiguiente = false;
          console.error('Error al crear cotización:', error);
          this.alert.show({
            title: this.translate.instant('PROFILE_PAGE.GENERIC_ERROR'),
            text: getApiErrorMessage(
              error,
              this.translate.instant('MODAL_RESERVATION.QUOTE_CREATE_RETRY'),
            ),
            confirmButtonText: this.translate.instant(
              'COMMON_ALERTS.BUTTON_OK',
            ),
          });
        },
      });
    } else {
      this.reservationStep++;
    }
  }

  onTimeSlotSelected(timeSlot: any) {
    this.selectedSlotId = timeSlot.id;
  }

  private getFromAmount(): number {
    if (!this.cotizacion) return 0;
    return this.exchangeRateCalculator.getFromAmountForQuote(this.cotizacion);
  }

  private getToAmount(): number {
    if (!this.cotizacion) return 0;
    return this.exchangeRateCalculator.getToAmountForQuote(this.cotizacion);
  }

  private getForeignAmountForReservation(): number {
    if (!this.cotizacion) {
      return 0;
    }

    const hasConfirmedAmounts =
      !!this.quotationId &&
      (this.confirmedFromAmount ?? 0) > 0 &&
      (this.confirmedToAmount ?? 0) > 0;

    return getForeignAmountFromCurrencyPair(
      this.cotizacion.from?.currency?.code,
      this.cotizacion.to?.currency?.code,
      hasConfirmedAmounts ? this.confirmedFromAmount! : this.getFromAmount(),
      hasConfirmedAmounts ? this.confirmedToAmount! : this.getToAmount(),
    );
  }

  get displayExchangeRate(): number {
    if (!this.queDivisaTienes || !this.queDivisaQuieres) {
      return 0;
    }

    return this.exchangeRateCalculator.convertToAmount(
      this.queDivisaTienes,
      this.queDivisaQuieres,
      1,
    );
  }

  get promoForeignCurrencyCode(): string {
    const fromCode = this.queDivisaTienes?.currency?.code?.toUpperCase();
    const toCode = this.queDivisaQuieres?.currency?.code?.toUpperCase();
    if (fromCode === 'MXN') {
      return toCode ?? '';
    }
    if (toCode === 'MXN') {
      return fromCode ?? '';
    }
    return toCode ?? fromCode ?? '';
  }

  get showOvernightNotice(): boolean {
    return (
      this.isOutOfHours &&
      !!this.overnightQuotationTimestamp &&
      !!this.overnightCollectionDayLabel &&
      !!this.overnightPickupTimeRange
    );
  }

  get overnightQuotationTimestamp(): string {
    return this.formatOvernightQuotationTimestamp(
      this.overnightServerDate,
      this.overnightServerTime,
    );
  }

  get overnightCollectionDayLabel(): string {
    return this.formatOvernightCollectionDayLabel(this.collectionDateRaw);
  }

  get overnightPickupTimeRange(): string {
    if (!this.availableTimeSlots.length) {
      return '';
    }

    const firstSlot = this.availableTimeSlots[0];
    const lastSlot =
      this.availableTimeSlots[this.availableTimeSlots.length - 1];

    if (!firstSlot?.timeFrom || !lastSlot?.timeto) {
      return '';
    }

    return `${firstSlot.timeFrom} a ${lastSlot.timeto}`;
  }

  private loadEstimatedPoints(): void {
    if (
      !this.cotizacion?.from?.currency ||
      !this.cotizacion?.to?.currency
    ) {
      this.estimatedPoints = 0;
      return;
    }
    const fromCode =
      this.cotizacion.from.currency.code ||
      this.cotizacion.from.currency.name ||
      '';
    const toCode =
      this.cotizacion.to.currency.code ||
      this.cotizacion.to.currency.name ||
      '';
    const amountReserved = this.getFromAmount();
    const fromAmount = amountReserved;
    const toAmount = this.getToAmount();
    const amountRate = fromAmount > 0 ? toAmount / fromAmount : 0;
    const fromBuyRate = Number(this.cotizacion?.from?.buy) || 0;
    const toSellRate = Number(this.cotizacion?.to?.sell) || 0;

    this.reservationsService
      .estimatePoints({
        currencyFromCode: fromCode,
        currencyToCode: toCode,
        amountReserved,
        amountRate,
        toAmount,
        fromBuyRate,
        toSellRate,
      })
      .subscribe({
        next: (res) => {
          const data = res?.data;
          this.estimatedPoints =
            data && !Array.isArray(data) && 'estimatedPoints' in data
              ? (data as { estimatedPoints: number }).estimatedPoints
              : 0;
        },
        error: () => {
          this.estimatedPoints = 0;
        },
      });
  }

  completar() {
    if (this.isProcessingCompletar) {
      return;
    }

    if (!this.isQuoteValid()) {
      this.showQuoteValidationError();
      return;
    }

    const currentUserStr = localStorage.getItem('current');
    const branchStr = localStorage.getItem('branch');

    // Validar usuario autenticado
    if (!currentUserStr) {
      const quotationIds = [this.quotationId, this.normalQuotationId].filter(
        (id): id is number => typeof id === 'number' && id > 0,
      );
      if (quotationIds.length) {
        savePendingQuotationIds(quotationIds);
      }

      const loginPromptData: LoginPromptDialogData = {
        isOutOfHours: this.isOutOfHours,
        confirmationDeadline: this.buildOvernightConfirmationDeadline(),
      };

      const dialogRef = this.dialog.open(LoginPromptComponent, {
        width: '100%',
        maxWidth: '481px',
        disableClose: true,
        panelClass: ['login-prompt-dialog', 'dialog-center'],
        data: loginPromptData,
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result === 'modify_amount') {
          this.reservationStep = 1;
          return;
        }
        if (result === 'login' || result === 'signup') {
          this._dialogRef.close(this.cotizacion);
        }
      });
      return;
    }

    // Parsear datos
    const currentUser = JSON.parse(currentUserStr);
    const branch = branchStr ? JSON.parse(branchStr) : null;

    // Validar datos requeridos del usuario
    if (!currentUser?.id) {
      this.alert.show({
        title: this.translate.instant('PROFILE_PAGE.GENERIC_ERROR'),
        text: this.translate.instant('MODAL_RESERVATION.USER_SESSION_RETRY'),
        confirmButtonText: this.translate.instant('COMMON_ALERTS.BUTTON_OK'),
      });
      return;
    }

    if (!currentUser?.name || !currentUser?.phone || !currentUser?.email) {
      this.alert.show({
        title: this.translate.instant('PROFILE_PAGE.GENERIC_ERROR'),
        text: this.translate.instant('MODAL_RESERVATION.PROFILE_INCOMPLETE'),
        confirmButtonText: this.translate.instant('COMMON_ALERTS.BUTTON_OK'),
      });
      return;
    }

    if (
      currentUser?.phonePendingVerification ||
      currentUser?.emailPendingVerification
    ) {
      this.alert.show({
        title: this.translate.instant('PROFILE_PAGE.GENERIC_ERROR'),
        text: this.translate.instant(
          'MODAL_RESERVATION.PROFILE_VERIFICATION_PENDING',
        ),
        confirmButtonText: this.translate.instant('COMMON_ALERTS.BUTTON_OK'),
      });
      return;
    }

    if (
      this.cotizacion &&
      this.exchangeRateCalculator.requiresVerifiedPhoneForQuote(
        this.cotizacion,
        this.getExchangesForValidation(),
        !!currentUser?.isPhoneVerified,
      )
    ) {
      void this.promptPhoneVerification();
      return;
    }

    if (!this.sucursal?.id) {
      this.alert.show({
        title: this.translate.instant('PROFILE_PAGE.GENERIC_ERROR'),
        text: this.translate.instant('MODAL_RESERVATION.NO_BRANCH_SELECTED'),
        confirmButtonText: this.translate.instant('COMMON_ALERTS.BUTTON_OK'),
      });
      return;
    }

    // Construir nombre completo del cliente
    const fullName = [
      currentUser.name,
      currentUser.lastName,
      currentUser.lastNameMother,
    ]
      .filter(Boolean)
      .join(' ')
      .trim();

    // Construir body según el nuevo DTO
    const foreignAmount = this.getPromoForeignAmount();
    const { promoAmount, normalAmount } =
      this.getPromoSplitAmounts(foreignAmount);

    const reservationBody: CreateReservationDto = {
      customer_name: fullName || currentUser.name,
      customer_phone: currentUser.phone,
      customer_email: currentUser.email,
      amount_rate: 0,
      client_id: currentUser.id,
      branch_id: branch?.id,
      currency_from_id: this.cotizacion.from.currency.id,
      currency_to_id: this.cotizacion.to.currency.id,
      amount_reserved: this.getForeignAmountForReservation(),
      address_id: this.sucursal.id,
      quotation_id: this.quotationId ?? this.normalQuotationId,
      request_id: this.ensureReservationRequestId(),
    };

    if (this.data.promotion && promoAmount > 0) {
      reservationBody.promotion_id = this.data.promotion.id;
      reservationBody.promotional_foreign_amount = foreignAmount;
      reservationBody.promotional_rate =
        this.promoAvailability?.effectivePromotionRate ?? undefined;
      reservationBody.promo_amount_reserved = promoAmount;
      if (normalAmount > 0) {
        reservationBody.normal_quotation_id = this.normalQuotationId;
        reservationBody.normal_amount_reserved = normalAmount;
      }
    }

    this.isProcessingCompletar = true;
    this.reservationsService.create(reservationBody).subscribe({
      next: (response) => {
        this.isProcessingCompletar = false;
        if (response.success) {
          // Tracking de analytics
          this._analyticsService.trackEvent(
            'RESERVACION',
            'COTIZADOR',
            `${this.cotizacion?.from.currency.code}_TO_${this.cotizacion?.to.currency.code}`,
          );

          this.clearReservationCache();

          const responseData = response.data as
            | { id?: number }
            | Array<{ id?: number }>
            | undefined;

          if (Array.isArray(responseData) && responseData.length > 0) {
            const firstId = responseData[0]?.id;
            if (firstId) {
              this.router.navigate(['/reservations', firstId], {
                state: { splitReservations: responseData },
              });
            } else {
              this.router.navigate(['/reservations']);
            }
          } else if (responseData && !Array.isArray(responseData)) {
            const reservationId = responseData.id;
            if (reservationId) {
              this.router.navigate(['/reservations', reservationId]);
            } else {
              this.router.navigate(['/reservations']);
            }
          } else {
            this.router.navigate(['/reservations']);
          }

          this._dialogRef.close(this.cotizacion);
        } else {
          this.alert.show({
            title: this.translate.instant('PROFILE_PAGE.GENERIC_ERROR'),
            text:
              response.message ||
              this.translate.instant(
                'MODAL_RESERVATION.RESERVATION_CREATE_FAILED',
              ),
            confirmButtonText: this.translate.instant(
              'COMMON_ALERTS.BUTTON_OK',
            ),
          });
        }
      },
      error: (error) => {
        this.isProcessingCompletar = false;
        console.error('Error al crear reservación:', error);
        if (this.isPhoneVerificationRequiredError(error)) {
          void this.promptPhoneVerification();
          return;
        }
        this.alert.show({
          title: this.translate.instant('PROFILE_PAGE.GENERIC_ERROR'),
          text: getApiErrorMessage(
            error,
            this.translate.instant(
              'MODAL_RESERVATION.RESERVATION_CREATE_FAILED',
            ),
          ),
          confirmButtonText: this.translate.instant('COMMON_ALERTS.BUTTON_OK'),
        });
      },
    });
  }

  recibirCotizacion(quoter: any) {
    if (!this.isQuotePayloadValid(quoter)) {
      return;
    }

    this.queDivisaQuieres = quoter.to;
    this.queDivisaTienes = quoter.from;
    this.cotizacion = quoter;
    this.quotationId = undefined;
    this.normalQuotationId = undefined;
    this.confirmedFromAmount = undefined;
    this.confirmedToAmount = undefined;
    this.reservationRequestId = undefined;
    this.updatePromoSplitPreview();
  }

  get quoteValidationErrorKey(): string | null {
    if (!this.cotizacion) {
      return null;
    }
    return this.getQuoteValidationErrorKey();
  }

  private async promptPhoneVerification(): Promise<void> {
    const result = await this.alert.show({
      title: this.translate.instant(
        'MODAL_RESERVATION.PHONE_VERIFICATION_TITLE',
      ),
      text: this.translate.instant(
        'MODAL_RESERVATION.PHONE_VERIFICATION_REQUIRED',
      ),
      confirmButtonText: this.translate.instant(
        'MODAL_RESERVATION.VERIFY_PHONE_BUTTON',
      ),
      showCancelButton: true,
      cancelButtonText: this.translate.instant('COMMON_ALERTS.BUTTON_OK'),
    });
    if (!result.isConfirmed) {
      return;
    }
    this._dialogRef.close();
    void this.router.navigate(['/profile-new'], {
      queryParams: { verifyPhone: '1' },
    });
  }

  private isPhoneVerificationRequiredError(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
      return false;
    }
    const body = (error as { error?: unknown }).error;
    if (!body || typeof body !== 'object') {
      return false;
    }
    const nested = (body as { error?: { message?: string } }).error;
    return nested?.message === 'PHONE_VERIFICATION_REQUIRED';
  }

  private showQuoteValidationError(errorKey?: string | null): void {
    const key =
      errorKey ??
      this.getQuoteValidationErrorKey() ??
      'MODAL_RESERVATION.INVALID_CURRENCY_PAIR';
    this.alert.show({
      title: this.translate.instant('PROFILE_PAGE.GENERIC_ERROR'),
      text: this.translate.instant(key),
      confirmButtonText: this.translate.instant('COMMON_ALERTS.BUTTON_OK'),
    });
  }

  private isQuotePayloadValid(quote: any): boolean {
    const fromId = quote?.from?.currency?.id;
    const toId = quote?.to?.currency?.id;
    if (!fromId || !toId || fromId === toId) {
      return false;
    }
    const amount = Number(quote?.amount) || 0;
    return amount > 0;
  }

  private isQuoteValid(): boolean {
    return this.getQuoteValidationErrorKey() === null;
  }

  private getQuoteValidationErrorKey(): string | null {
    if (!this.isQuotePayloadValid(this.cotizacion)) {
      return 'MODAL_RESERVATION.INVALID_CURRENCY_PAIR';
    }

    const fromAmount = this.getFromAmount();
    const toAmount = this.getToAmount();
    if (fromAmount <= 0 || toAmount <= 0) {
      return 'MODAL_RESERVATION.INVALID_CURRENCY_PAIR';
    }

    if (
      !this.exchangeRateCalculator.meetsMinimumReservationUsd(
        this.cotizacion,
        this.getExchangesForValidation(),
      )
    ) {
      return 'MODAL_RESERVATION.MIN_RESERVATION_USD';
    }

    return null;
  }

  private getExchangesForValidation(): IExchangeModel[] {
    return Array.isArray(this.divisas) ? this.divisas : [];
  }

  changeDate(date: number): any {
    return moment(date).format('yyyy/MM/DD');
  }

  onBranchSelected(branch: IBranchModel): void {
    this.sucursal = branch;
    this.quotationId = undefined;
    this.normalQuotationId = undefined;
    this.confirmedFromAmount = undefined;
    this.confirmedToAmount = undefined;
    this.selectedSlotId = '';
    localStorage.setItem('branch', JSON.stringify(branch));
    localStorage.setItem('branchId', branch.id.toString());
    this.generateAvailableTimeSlots();
    this.updateExchangeRatesForBranch(branch.id);
  }

  getSelectedTimeSlot(): any {
    if (!this.selectedSlotId || !this.availableTimeSlots.length) {
      return null;
    }
    return (
      this.availableTimeSlots.find((slot) => slot.id === this.selectedSlotId) ||
      null
    );
  }

  private generateAvailableTimeSlots(inventory?: {
    currencyCode?: string;
    requestedAmount?: number;
  }): void {
    if (!this.sucursal?.id) {
      this.availableTimeSlots = [];
      this.marketInactive = false;
      this.branchTimezone = MEXICO_TIMEZONE;
      return;
    }

    const days = environment.PRODUCION ? 1 : 2;
    this.operatingHoursService
      .getAvailableTimeSlots(this.sucursal.id, days, inventory)
      .subscribe({
        next: (payload) => this.applyAvailableSlots(payload),
        error: (error) => {
          console.error('Error al obtener horarios de operación:', error);
          this.availableTimeSlots = [];
          this.isOutOfHours = false;
          this.marketInactive = false;
          this.branchTimezone = MEXICO_TIMEZONE;
          this.collectionDateLabel = '';
          this.collectionDateRaw = '';
          this.overnightServerDate = '';
          this.overnightServerTime = '';
        },
      });
  }

  private applyAvailableSlots(payload: {
    slots?: any[];
    timezone?: string;
    isOutOfHours?: boolean;
    quoterEnabled?: boolean;
    exceedsStock?: boolean;
    collectionDate?: string | null;
    serverDate?: string;
    serverTime?: string;
    serverDateTime?: string;
  }): void {
    this.branchTimezone = payload.timezone || MEXICO_TIMEZONE;
    this.availableTimeSlots = payload.slots ?? [];
    this.isOutOfHours = payload.isOutOfHours ?? false;
    this.marketInactive = payload.quoterEnabled === false;
    this.stockApprovalRequired = Boolean(
      payload.exceedsStock && this.isOutOfHours,
    );
    this.collectionDateRaw = payload.collectionDate ?? '';
    this.overnightServerDate = payload.serverDate ?? '';
    this.overnightServerTime = payload.serverTime ?? '';
    this.collectionDateLabel = this.formatCollectionDateLabel(
      payload.collectionDate,
    );
    if (payload.serverDate && payload.serverTime) {
      this.quotationDate = this.formatQuotationDate(
        payload.serverDate,
        payload.serverTime,
      );
    } else if (payload.serverDateTime) {
      this.quotationDate = this.formatQuotationDate();
    }
    if (
      this.selectedSlotId &&
      !this.availableTimeSlots.some((slot) => slot.id === this.selectedSlotId)
    ) {
      this.selectedSlotId = '';
    }
  }

  private formatOvernightQuotationTimestamp(
    serverDate?: string,
    serverTime?: string,
  ): string {
    if (!serverDate || !serverTime) {
      return '';
    }

    const [year, month, day] = serverDate.split('-').map(Number);
    const [hour, minute] = serverTime.split(':').map(Number);

    if (
      ![year, month, day, hour, minute].every((value) => Number.isFinite(value))
    ) {
      return '';
    }

    const dateReference = new Date(year, month - 1, day, 12, 0, 0);
    const monthName = new Intl.DateTimeFormat('es-MX', {
      month: 'long',
    })
      .format(dateReference)
      .replace(/^\w/, (char) => char.toUpperCase());

    const normalizedHour = hour.toString().padStart(2, '0');
    const normalizedMinute = minute.toString().padStart(2, '0');

    return `${day}/${monthName}/${year}, ${normalizedHour}:${normalizedMinute} hrs`;
  }

  private formatOvernightCollectionDayLabel(collectionDate?: string): string {
    if (!collectionDate) {
      return '';
    }

    const [year, month, day] = collectionDate.split('-').map(Number);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
      return '';
    }

    const dateReference = new Date(year, month - 1, day, 12, 0, 0);
    const weekday = new Intl.DateTimeFormat('es-MX', {
      weekday: 'long',
    }).format(dateReference);
    const monthShort = new Intl.DateTimeFormat('es-MX', {
      month: 'short',
    })
      .format(dateReference)
      .replace('.', '')
      .replace(/^\w/, (char) => char.toUpperCase());
    const yearShort = String(year).slice(-2);

    return `${weekday} ${day}/${monthShort}/${yearShort}`;
  }

  private formatCollectionDateLabel(collectionDate?: string | null): string {
    if (!collectionDate) {
      return '';
    }

    const [year, month, day] = collectionDate.split('-').map(Number);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
      return '';
    }

    const dateReference = new Date(year, month - 1, day, 12, 0, 0);
    const lang =
      this.translate.currentLang ||
      this.translate.defaultLang ||
      'es';

    if (lang.startsWith('en')) {
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }).format(dateReference);
    }

    const weekday = new Intl.DateTimeFormat('es-MX', {
      weekday: 'long',
    }).format(dateReference);
    const monthName = new Intl.DateTimeFormat('es-MX', {
      month: 'long',
    }).format(dateReference);

    return `${this.capitalizeLabelToken(weekday)} ${day} de ${this.capitalizeLabelToken(monthName)} ${year}`;
  }

  private capitalizeLabelToken(value: string): string {
    if (!value) {
      return value;
    }
    return value.replace(/^\w/, (char) => char.toUpperCase());
  }

  private buildOvernightConfirmationDeadline(): string {
    if (!this.isOutOfHours || !this.collectionDateRaw) {
      return '';
    }

    const firstSlot = this.availableTimeSlots[0];
    const lang =
      this.translate.currentLang ||
      this.translate.defaultLang ||
      'es';

    return formatOvernightConfirmationDeadline(
      this.collectionDateRaw,
      firstSlot?.timeFrom,
      lang,
    );
  }

  private formatQuotationDate(serverDate?: string, serverTime?: string): string {
    if (serverDate && serverTime) {
      const [year, month, day] = serverDate.split('-').map(Number);
      const [hour, minute] = serverTime.split(':').map(Number);

      if (
        Number.isFinite(year) &&
        Number.isFinite(month) &&
        Number.isFinite(day) &&
        Number.isFinite(hour) &&
        Number.isFinite(minute)
      ) {
        const dateWithMexicoClock = new Date(year, month - 1, day, hour, minute);
        const formattedDate = new Intl.DateTimeFormat('es-MX', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }).format(dateWithMexicoClock);
        const normalizedHour = hour.toString().padStart(2, '0');
        const normalizedMinute = minute.toString().padStart(2, '0');

        return `${formattedDate}, ${normalizedHour}:${normalizedMinute} hrs`;
      }
    }

    return (
      new Intl.DateTimeFormat('es-MX', {
        timeZone: this.branchTimezone,
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date()) + ' hrs'
    );
  }

  private updateExchangeRatesForBranch(branchId: number): void {
    this.exchangeService
      .getBranchExchangeRates(branchId, this.sucursal.name)
      .subscribe({
        next: (branchExchange) => {
          const newExchangeRates = branchExchange.exchange;
          this.divisas = newExchangeRates;

          const previousFromCurrencyId = this.queDivisaTienes?.currency?.id;
          const previousToCurrencyId = this.queDivisaQuieres?.currency?.id;

          const newFromExchange = newExchangeRates.find(
            (ex: IExchangeModel) => ex.currency?.id === previousFromCurrencyId,
          );
          const newToExchange = newExchangeRates.find(
            (ex: IExchangeModel) => ex.currency?.id === previousToCurrencyId,
          );

          if (newFromExchange) {
            this.queDivisaTienes = newFromExchange;
          }

          if (newToExchange) {
            this.queDivisaQuieres = newToExchange;
          }

          if (this.cotizacion && newFromExchange && newToExchange) {
            this.recalculateQuotation(newFromExchange, newToExchange);
          }
        },
        error: (error) => {
          console.error(
            'Error al obtener tasas de cambio de la sucursal:',
            error,
          );
        },
      });
  }

  private recalculateQuotation(
    newFromExchange: IExchangeModel,
    newToExchange: IExchangeModel,
  ): void {
    if (!this.cotizacion) {
      return;
    }

    const previousType = this.cotizacion.type || 'from';
    const previousAmount = this.cotizacion.amount || 0;

    if (previousAmount > 0) {
      this.cotizacion = {
        ...this.cotizacion,
        from: newFromExchange,
        to: newToExchange,
        type: previousType,
        amount: previousAmount,
      };
    } else {
      this.cotizacion = {
        ...this.cotizacion,
        from: newFromExchange,
        to: newToExchange,
      };
    }
  }

  private createQuotationIfNeeded(): Observable<any> {
    if (this.data.promotion) {
      return this.createPromotionQuotationsIfNeeded();
    }

    if (this.quotationId) {
      return of({ success: true });
    }

    if (!this.isQuoteValid()) {
      return throwError(() => ({
        message: this.translate.instant(
          this.getQuoteValidationErrorKey() ??
            'MODAL_RESERVATION.INVALID_CURRENCY_PAIR',
        ),
      }));
    }

    if (!this.cotizacion || !this.sucursal?.id) {
      return throwError(() => ({
        message: this.translate.instant(
          'MODAL_RESERVATION.INCOMPLETE_QUOTE_PAYLOAD',
        ),
      }));
    }

    const selectedTimeSlot = this.getSelectedTimeSlot();
    const cotizacionAny = this.cotizacion as any;
    const quotationType = cotizacionAny.type || 'from';
    const quotationAmount = cotizacionAny.amount || 0;

    const createDto: any = {
      branchId: this.sucursal.id,
      currencyFromId: this.cotizacion.from.currency.id,
      currencyToId: this.cotizacion.to.currency.id,
      inventoryAmount: this.getToAmount(),
      collectionScheduleStart: mexicoWallClockToIso(
        selectedTimeSlot?.date,
        selectedTimeSlot?.timeFrom,
        this.branchTimezone,
      ),
      collectionScheduleEnd: mexicoWallClockToIso(
        selectedTimeSlot?.date,
        selectedTimeSlot?.timeto,
        this.branchTimezone,
      ),
    };

    if (quotationType === 'from') {
      createDto.fromAmount = quotationAmount;
    } else {
      createDto.toAmount = quotationAmount;
    }

    return this.quotationsService.createQuotation(createDto).pipe(
      map((response: any) => {
        if (response.success && response.data) {
          this.quotationId = response.data.id;
          this.confirmedFromAmount = Number(response.data.fromAmount) || 0;
          this.confirmedToAmount = Number(response.data.toAmount) || 0;
        }
        return response;
      }),
    );
  }

  private clearReservationCache(): void {
    clearPendingQuotationIds();
    this.quotationId = undefined;
    this.normalQuotationId = undefined;
    this.confirmedFromAmount = undefined;
    this.confirmedToAmount = undefined;
    this.reservationRequestId = undefined;
  }

  private ensureReservationRequestId(): string {
    if (!this.reservationRequestId) {
      this.reservationRequestId = createReservationIdempotencyKey();
    }
    return this.reservationRequestId;
  }

  private initPromotionFlow(promotion: PromotionDetailPromotion): void {
    const branchId = this.sucursal?.id ?? promotion.branch?.id;
    if (!branchId || !promotion.id) {
      return;
    }

    forkJoin({
      branchExchange: this.exchangeService.getBranchExchangeRates(
        branchId,
        this.sucursal?.name,
      ),
      availability: this.httpService.getPromotionAvailability(promotion.id),
    }).subscribe({
      next: ({ branchExchange, availability }) => {
        this.divisas = branchExchange.exchange;
        const mxn = branchExchange.exchange.find(
          (exchange) => exchange.currency?.code === 'MXN',
        );
        const promoRateId = promotion.exchangeRate?.id;
        let promoExchange = branchExchange.exchange.find(
          (exchange) => exchange.id === promoRateId,
        );
        if (!promoExchange && promotion.exchangeRate) {
          promoExchange = mapPromotionExchangeToModel(promotion.exchangeRate);
        }

        if (!mxn || !promoExchange || !promotion.operationType) {
          return;
        }

        const effectiveExchange = applyEffectivePromotionRate(
          promoExchange,
          promotion.operationType,
          Number(promotion.promotionPrice ?? 0),
        );

        if (promotion.operationType === 'buy') {
          this.queDivisaTienes = mxn;
          this.queDivisaQuieres = effectiveExchange;
        } else {
          this.queDivisaTienes = effectiveExchange;
          this.queDivisaQuieres = mxn;
        }

        const availabilityPayload = (availability as { data?: PromotionAvailabilityView })
          ?.data;
        this.promoAvailability = availabilityPayload ?? undefined;
        this.isPromoLocked = true;
        this.updatePromoSplitPreview();
      },
      error: (error) => {
        console.error('Error al cargar promoción:', error);
      },
    });
  }

  private createPromotionQuotationsIfNeeded(): Observable<any> {
    const promoQuoteReady =
      !this.getPromoSplitAmounts(this.getPromoForeignAmount()).promoAmount ||
      !!this.quotationId;
    const normalQuoteReady =
      !this.getPromoSplitAmounts(this.getPromoForeignAmount()).normalAmount ||
      !!this.normalQuotationId;

    if (promoQuoteReady && normalQuoteReady) {
      return of({ success: true });
    }

    if (!this.cotizacion || !this.sucursal?.id || !this.data.promotion) {
      return throwError(() => ({
        message: this.translate.instant(
          'MODAL_RESERVATION.INCOMPLETE_QUOTE_PAYLOAD',
        ),
      }));
    }

    const totalForeignAmount = this.getPromoForeignAmount();
    const { promoAmount, normalAmount } =
      this.getPromoSplitAmounts(totalForeignAmount);
    const scheduleFields = this.buildQuotationScheduleFields();
    const currencyFields = {
      branchId: this.sucursal.id,
      currencyFromId: this.cotizacion.from.currency.id,
      currencyToId: this.cotizacion.to.currency.id,
    };

    const requests: Observable<any>[] = [];

    if (promoAmount > 0 && !this.quotationId) {
      requests.push(
        this.quotationsService
          .createQuotation({
            ...currencyFields,
            ...scheduleFields,
            ...this.buildPromotionQuotationAmountFields(
              promoAmount,
              totalForeignAmount,
            ),
            inventoryAmount: this.getToAmount(),
            promotionId: this.data.promotion.id,
          })
          .pipe(
            tap((response) => {
              if (response.success && response.data?.id) {
                this.quotationId = response.data.id;
              }
            }),
          ),
      );
    }

    if (normalAmount > 0 && !this.normalQuotationId) {
      requests.push(
        this.quotationsService
          .createQuotation({
            ...currencyFields,
            ...scheduleFields,
            ...this.buildPromotionQuotationAmountFields(
              normalAmount,
              totalForeignAmount,
            ),
            inventoryAmount: this.getToAmount(),
          })
          .pipe(
            tap((response) => {
              if (response.success && response.data?.id) {
                this.normalQuotationId = response.data.id;
              }
            }),
          ),
      );
    }

    if (!requests.length) {
      return of({ success: true });
    }

    return forkJoin(requests).pipe(
      map((responses) => ({
        success: responses.every((response) => response.success),
        message: responses.find((response) => !response.success)?.message,
      })),
    );
  }

  private buildQuotationScheduleFields(): Partial<CreateQuotationDto> {
    const selectedTimeSlot = this.getSelectedTimeSlot();
    return {
      collectionScheduleStart: mexicoWallClockToIso(
        selectedTimeSlot?.date,
        selectedTimeSlot?.timeFrom,
        this.branchTimezone,
      ),
      collectionScheduleEnd: mexicoWallClockToIso(
        selectedTimeSlot?.date,
        selectedTimeSlot?.timeto,
        this.branchTimezone,
      ),
    };
  }

  private buildPromotionQuotationAmountFields(
    foreignSlice: number,
    totalForeignAmount: number,
  ): Partial<CreateQuotationDto> {
    const operationType = this.data.promotion?.operationType;
    const quoteType =
      (this.cotizacion as { type?: 'from' | 'to' })?.type ?? 'from';

    if (
      !operationType ||
      foreignSlice <= 0 ||
      totalForeignAmount <= 0 ||
      foreignSlice > totalForeignAmount
    ) {
      return operationType === 'buy'
        ? { toAmount: foreignSlice }
        : { fromAmount: foreignSlice };
    }

    const sliceRatio = foreignSlice / totalForeignAmount;

    if (operationType === 'buy') {
      if (quoteType === 'from') {
        return {
          fromAmount: Number((this.getFromAmount() * sliceRatio).toFixed(2)),
        };
      }
      return { toAmount: foreignSlice };
    }

    if (quoteType === 'to') {
      return {
        toAmount: Number((this.getToAmount() * sliceRatio).toFixed(2)),
      };
    }
    return { fromAmount: foreignSlice };
  }

  private getPromoForeignAmount(): number {
    if (!this.cotizacion || !this.data.promotion?.exchangeRate?.currency?.id) {
      return 0;
    }

    const promoCurrencyId = this.data.promotion.exchangeRate.currency.id;
    const fromId = this.cotizacion.from?.currency?.id;
    const toId = this.cotizacion.to?.currency?.id;

    if (fromId === promoCurrencyId) {
      return this.exchangeRateCalculator.getFromAmountForQuote(this.cotizacion);
    }
    if (toId === promoCurrencyId) {
      return this.exchangeRateCalculator.getToAmountForQuote(this.cotizacion);
    }
    return 0;
  }

  private getPromoSplitAmounts(foreignAmount: number): PromoSplitPreview {
    const available = this.promoAvailability?.availableAmount ?? 0;
    const currencyCode = this.promoAvailability?.currencyCode ?? '';
    const promoRate = this.promoAvailability?.effectivePromotionRate ?? null;
    const regularRate = this.promoAvailability?.regularRate ?? null;

    let promoAmount = 0;
    let normalAmount = foreignAmount;
    if (available > 0) {
      promoAmount = Math.min(foreignAmount, available);
      normalAmount = Math.max(foreignAmount - promoAmount, 0);
    }

    const totalAmount = promoAmount + normalAmount;
    let averageRate: number | null = null;
    if (
      totalAmount > 0 &&
      promoRate != null &&
      regularRate != null
    ) {
      averageRate =
        (promoAmount * promoRate + normalAmount * regularRate) / totalAmount;
    }

    return {
      promoAmount,
      normalAmount,
      totalAmount,
      currencyCode,
      promoRate,
      regularRate,
      averageRate,
    };
  }

  private updatePromoSplitPreview(): void {
    if (!this.data.promotion || !this.cotizacion) {
      this.promoSplitPreview = undefined;
      this.showPromoSplitWarning = false;
      return;
    }
    const foreignAmount = this.getPromoForeignAmount();
    this.promoSplitPreview = this.getPromoSplitAmounts(foreignAmount);
    if (!this.hasPromoSplit) {
      this.showPromoSplitWarning = false;
    }
  }

  private proceedFromQuoteStep(): void {
    this.isProcessingSiguiente = true;
    const toCurrencyCode =
      this.cotizacion?.to?.currency?.code?.toUpperCase() ??
      this.queDivisaQuieres?.currency?.code?.toUpperCase();
    const toAmount = this.getToAmount();

    this.branchesService
      .checkInventoryAvailability(
        this.sucursal.id,
        toCurrencyCode ?? '',
        toAmount,
      )
      .subscribe({
        next: (hasEnoughStock) => {
          this.isProcessingSiguiente = false;

          if (!hasEnoughStock && this.isOutOfHours) {
            this.isProcessingSiguiente = true;
            this.stockApprovalRequired = true;
            this.operatingHoursService
              .getAvailableTimeSlots(this.sucursal.id, environment.PRODUCION ? 1 : 2, {
                currencyCode: toCurrencyCode,
                requestedAmount: toAmount,
              })
              .subscribe({
                next: (payload) => {
                  this.isProcessingSiguiente = false;
                  this.applyAvailableSlots(payload);
                  this.showPromoSplitWarning = false;
                  this.reservationStep++;
                },
                error: () => {
                  this.isProcessingSiguiente = false;
                  this.alert.show({
                    title: this.translate.instant('INVENTORY.INSUFFICIENT_TITLE'),
                    text: this.translate.instant('INVENTORY.APPROVAL_WINDOW'),
                    confirmButtonText: this.translate.instant(
                      'COMMON_ALERTS.BUTTON_OK',
                    ),
                  });
                },
              });
            return;
          }

          this.stockApprovalRequired = false;
          this.showPromoSplitWarning = false;
          this.reservationStep++;
        },
        error: () => {
          this.isProcessingSiguiente = false;
          this.showPromoSplitWarning = false;
          this.reservationStep++;
        },
      });
  }

  private handleExpiredQuotation(): void {
    this.clearReservationCache();
    this.quotationId = undefined;

    this.reservationStep = 1;
    this.cotizacion = undefined;

    if (this.sucursal?.id) {
      this.updateExchangeRatesForBranch(this.sucursal.id);
    }

    this.alert.show({
      title: this.translate.instant('MODAL_RESERVATION.QUOTE_EXPIRED_TITLE'),
      text: this.translate.instant('MODAL_RESERVATION.QUOTE_EXPIRED_TEXT'),
      confirmButtonText: this.translate.instant('COMMON_ALERTS.BUTTON_OK'),
    });
  }
}
